import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useGetBeneficiaries } from "@/hooks/use-queries";
import { lookupUser } from "@/lib/api";
import { getInitials } from "@/lib/utils";
import { useModalStore } from "@/zustand/modalStore";
import { ChevronDown, Zap } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import Throbber from "@/components/throbber";

const iconMap: Record<string, string> = {
  USDT: "/icons/usdt.svg",
  USDC: "/icons/usdc.svg",
};

const networkIconMap: Record<string, string> = {
  TRC20: "/icons/trc20.png",
  ERC20: "/icons/erc20.svg",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fullNameOf = (payee: Beneficiary) =>
  `${payee.firstName} ${payee.lastName}`.trim();

const Transfer2User = ({
  step,
  setStep,
}: {
  step: number;
  setStep: (step: number) => void;
}) => {
  const [email, setEmail] = useState("");
  const [payee, setPayee] = useState<Beneficiary | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);

  const [asset, setAsset] = useState<any>(null);
  const [amount, setAmount] = useState("");
  const { openModal, closeModal } = useModalStore();

  const { data: beneficiaries } = useGetBeneficiaries();

  /* Debounced email → user lookup. `lookupSeq` is the out-of-order guard: a
     slow earlier response must not overwrite a newer one, which an abort alone
     doesn't cover because the abort only fires while the request is in flight. */
  const lookupSeq = useRef(0);

  useEffect(() => {
    if (payee && payee.email === email) return;

    setPayee(null);
    setLookupFailed(false);

    if (!EMAIL_RE.test(email)) {
      setIsLookingUp(false);
      return;
    }

    const seq = ++lookupSeq.current;
    const controller = new AbortController();
    setIsLookingUp(true);

    const timer = setTimeout(async () => {
      try {
        const found = await lookupUser(email, controller.signal);
        if (seq !== lookupSeq.current) return;
        setPayee(found);
      } catch {
        // 404 is the common case — stay quiet, the user is mid-typing.
        if (seq !== lookupSeq.current) return;
        setLookupFailed(true);
      } finally {
        if (seq === lookupSeq.current) setIsLookingUp(false);
      }
    }, 450);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // `payee` is intentionally omitted: setting it inside would re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  // A beneficiary is already the shape lookup returns, so picking one skips it.
  const selectBeneficiary = (beneficiary: Beneficiary) => {
    setPayee(beneficiary);
    setEmail(beneficiary.email);
    setLookupFailed(false);
  };

  const selectSubWallet = () =>
    openModal("sendToUser", {
      email,

      onSelect: (selected: any) => {
        setAsset(selected);
        closeModal();
        setStep(2);
      },
    });

  if (step === 2 && payee) {
    const payeeName = fullNameOf(payee);
    const label = asset ? `${asset.token.type}-${asset.token.symbol}` : "";
    const symbol = asset?.token.symbol ?? "";
    const balance = Number(asset?.balance ?? 0);
    const icon = iconMap[asset?.token.symbol] ?? "/icons/usdt.svg";
    const networkIcon = networkIconMap[asset?.token.type];

    return (
      <div className="text-dark-text-50 mt-4 flex min-h-[70vh] flex-col px-2">
        <h2 className="font-semibold">Send to {payeeName.split(" ")[0]}</h2>

        <div className="mt-4 flex items-center justify-between gap-4">
          <div>
            <p>{payeeName}</p>
            <p className="text-dark-text-300 text-sm">{email}</p>
          </div>
          <div className="bg-dark-primary-gradient text-dark-text-400 flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-medium">
            {getInitials(payeeName)}
          </div>
        </div>

        <div className="mt-10 flex flex-col items-center">
          <p className="text-sm">Payee will receive</p>

          <button
            type="button"
            onClick={selectSubWallet}
            className="mt-4 flex cursor-pointer items-center gap-2.5"
          >
            <div className="relative size-8 shrink-0">
              <img src={icon} alt={label} className="size-full" />
              {networkIcon && (
                <img
                  src={networkIcon}
                  alt={asset?.token.type}
                  className="absolute -right-1 -bottom-1 size-4 rounded-full"
                />
              )}
            </div>
            <span className="text-xl">{label}</span>
            <ChevronDown className="size-5" />
          </button>

          <Input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
            placeholder="0"
            className={`placeholder:text-dark-text-300 mt-6 h-auto border-none bg-transparent p-0 text-center !text-4xl font-light shadow-none focus-visible:ring-0 ${amount === "" ? "text-dark-text-300" : "text-dark-text-50"}`}
          />
          <p className="text-dark-text-300 mt-1 text-sm">
            ≈{amount === "" ? 0 : amount}
          </p>
        </div>

        <div className="border-dark-stroke-3 mt-8 space-y-3 rounded-xl border p-4 text-sm">
          <div className="flex items-center justify-between gap-4">
            <span className="text-dark-text-300">{label} Balance</span>
            <span className="flex items-center gap-2.5">
              {balance.toFixed(2)} {symbol}
              <button
                type="button"
                onClick={() => setAmount(String(balance))}
                className="text-dark-primary-main cursor-pointer"
              >
                Max
              </button>
            </span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-dark-text-300">Send fee (0%)</span>
            <span>0 {symbol}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-dark-text-300">Payee will receive</span>
            <span>
              {amount === "" ? 0 : amount} {symbol}
            </span>
          </div>
        </div>

        <div className="mt-auto pt-10">
          <p className="flex items-center justify-center gap-2 text-sm">
            <Zap className="size-4 fill-current" />
            Usually arrives in less than 5 mins.
          </p>

          <Button
            onClick={() =>
              openModal("confirmPayment", {
                payee,
                amount,
                asset,
                // Land back on the entry screen, not this pre-filled form.
                onDone: () => {
                  setAmount("");
                  setStep(1);
                },
              })
            }
            disabled={Number(amount) <= 0 || Number(amount) > balance}
            className="text-dark-text-400 bg-dark-primary-gradient mt-4 h-12 w-full rounded-lg disabled:opacity-50"
          >
            Continue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="text-dark-text-50 mt-4 px-2">
      <h2 className="text-2xl font-semibold">Send to PayG8 User</h2>

      <div className="mt-6 flex flex-col gap-2.5">
        <Label className="text-dark-text-300 text-sm font-normal">Email</Label>
        <div className="relative">
          <Input
            onChange={(e) => setEmail(e.target.value)}
            value={email}
            className="field-gradient-aux-4 text-dark-text-50 placeholder:text-dark-text-300 h-12 rounded-lg px-4 text-sm placeholder:text-sm"
            type="email"
            placeholder="PayG8 user email"
          />
          {isLookingUp && (
            <Throbber className="absolute top-1/2 right-4 size-4 -translate-y-1/2" />
          )}
        </div>
        {lookupFailed && (
          <p className="text-dark-error-300 text-xs">
            No PayG8 account found for this email
          </p>
        )}
      </div>

      {payee && (
        <div className="mt-4 flex flex-col gap-2.5">
          <Label className="text-dark-text-300 text-sm font-normal">
            Payee full name
          </Label>
          <p className="field-gradient-aux-4 flex h-12 items-center rounded-lg px-4 text-sm">
            {fullNameOf(payee)}
          </p>
          <p className="text-dark-text-300 text-xs">
            Please verify the recipient&apos;s details before continuing.
            Transfers to the wrong account may not be reversible.
          </p>
        </div>
      )}

      <Button
        disabled={!payee}
        onClick={selectSubWallet}
        className="text-dark-text-400 bg-dark-primary-gradient mt-6 h-12 w-full rounded-lg disabled:opacity-50"
      >
        Continue
      </Button>

      <div className="mt-8">
        <h3 className="text-sm">Recent</h3>

        {beneficiaries && beneficiaries.length > 0 ? (
          <div className="mt-4 flex flex-col">
            {beneficiaries.map((beneficiary) => (
              <BeneficiaryRow
                key={beneficiary.id}
                beneficiary={beneficiary}
                onSelect={() => selectBeneficiary(beneficiary)}
              />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center text-center">
            <h2 className="text-2xl font-bold">No Transfers Yet</h2>
            <p className="text-dark-text-300 mt-2 text-sm">
              You haven&apos;t sent money to any PayG8 user yet. Your transfers
              will appear here once you send money.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const BeneficiaryRow = ({
  beneficiary,
  onSelect,
}: {
  beneficiary: Beneficiary;
  onSelect: () => void;
}) => {
  const name = `${beneficiary.firstName} ${beneficiary.lastName}`.trim();
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full cursor-pointer items-center gap-4 py-3 text-left transition-all active:scale-99"
    >
      <div className="bg-dark-primary-gradient text-dark-text-400 flex size-12 shrink-0 items-center justify-center rounded-full font-medium">
        {getInitials(name)}
      </div>
      <div className="min-w-0">
        <p className="text-xl">{name}</p>
        <p className="text-dark-text-300 truncate text-sm">
          {beneficiary.email}
        </p>
      </div>
    </button>
  );
};

export default Transfer2User;
